<script setup>
// X178 (Grant 2026-10-27: "the setup assistant should be full width with the draft being floating and expandable"): the
// conversation takes the page and the draft floats over the bottom of the transcript, just above the composer. Collapsed it is
// a compact bar ("Draft · 4 changes", the latest change on one line, a chevron), bottom-right on desktop and the column's
// width on a phone; it pulses once, with a dot, when a turn changes the draft. Tapped, it opens the full draft (the default
// slot: the page's SetupDraftPanel and club notes) as a tall panel anchored bottom-right on desktop, a bottom sheet on a phone,
// scrolling inside; the slot's collapse control, Escape and a click outside collapse it. The same pattern as SetupGuideDock (X176).
// It shows only while `visible` (a draft, or remembered notes, to show), positioned inside the page's transcript stage; it stays
// mounted otherwise so the first draft a turn makes pulses too.
// Grant 2026-11-03 ("the draft box should have some sort of pulsing animation around it signalling that Review and apply is
// within that expandable section"): once the assistant says the draft is ready (proposal.ready, the server's ReadyForReview
// turn), the collapsed bar wears a brand outline and a soft ring that grows and fades around it, on a loop, with
// "Ready · Review and apply" as its line; it stops for good once the draft is opened (the button is inside). A reader who
// asked for reduced motion gets the still outline alone (useReducedMotion, as Tappy does). The box stays solid white with
// its shadow: the ring is a pseudo-element behind the border, never the bar's own box-shadow.
// Grant 2026-11-03 ("Review and apply needs to be fixed at the bottom of the Draft popup, it's hidden below the scroll"):
// the open panel is TlDialog's shape, a body that scrolls (the default slot) over a pinned foot (the `foot` slot, the
// page's SetupDraftFoot), so the button shows at every height, phone sheet included.
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {draftCheckLabel, draftDockLine, draftDockTile, draftDockTitle, draftHasContent, draftSignature, latestDraftStep} from '~/utils/setupAssistant';
import {useFootControls, useFootRoom} from '~/composables/useFootControls';
import {useReducedMotion} from '~/composables/useReducedMotion';

const props = defineProps({
  visible: {type: Boolean, default: true},
  proposal: {type: Object, default: null},
  question: {type: Object, default: null},
  applied: {type: Boolean, default: false},
  undone: {type: Boolean, default: false},
  notesCount: {type: Number, default: 0},
});

const expanded = ref(false);
const fresh = ref(false);
const seen = ref(false);
const pulse = ref(0);
const latest = ref(latestDraftStep(null, props.proposal));
watch(() => props.proposal, (next, previous) => {
  latest.value = latestDraftStep(previous ?? null, next);
  // A turn that changed what the draft shows: the collapsed bar pulses and carries a dot until it is opened.
  // X182 (R80 L3): every change pulses again, also while the dot is still showing from an earlier one (the key restarts the animation).
  if (draftHasContent(next) && draftSignature(previous) !== draftSignature(next) && !expanded.value) {
    fresh.value = true;
    pulse.value += 1;
  }
  if (!next) fresh.value = false;
});
// Nothing left to show (Start again, a discarded draft): it closes.
watch(() => props.visible, (v) => { if (!v) { expanded.value = false; fresh.value = false; seen.value = false; } });

// Ready for review: the ring runs until the draft is opened; a draft that goes back to not-ready and ready again rings again.
const reducedMotion = useReducedMotion();
const ready = computed(() => !!props.proposal?.ready && draftHasContent(props.proposal) && !props.applied && !props.undone);
watch(ready, (now, before) => { if (now && !before) seen.value = expanded.value; });
const ringing = computed(() => ready.value && !seen.value && !expanded.value);
const pulsing = computed(() => ringing.value && !reducedMotion.value);
const still = computed(() => ringing.value && reducedMotion.value);

const title = computed(() => draftDockTitle(props.proposal, {applied: props.applied, undone: props.undone, notesCount: props.notesCount}));
const line = computed(() => (ready.value ? `Ready · ${draftCheckLabel(props.proposal)}` : draftDockLine(latest.value, props.proposal, props.applied || props.undone ? null : props.question)));
// Grant 2026-11-03: a draft of one competition wears that format's icon in its colour (utils/formatExplainers) on the bar too.
const tile = computed(() => draftDockTile(props.proposal));

const expand = () => { expanded.value = true; fresh.value = false; seen.value = true; };
const collapse = () => { expanded.value = false; };
const onKey = (e) => { if (e?.key === 'Escape' && expanded.value) collapse(); };
// X223 (R92-M1): the open draft pauses the assistant's phone coach mark (it would sit above this panel and its scrim).
const {setCover, release} = useFootControls();
watch(expanded, (open) => setCover('setup-draft-dock', open));
// X224 (R93-M1): the collapsed bar is not position: fixed, so it reports its room itself (as the Setup guide bar does):
// a coach mark on a phone then sits above it instead of over it. Re-measured when the stage it floats in resizes (the
// composer below growing moves the bar up without changing its size). Expanded or hidden: no bar, 0 room.
const bar = ref(null);
useFootRoom('setup-draft-dock', bar, {alsoObserve: (el) => el.closest('.sdd')?.offsetParent ?? null});
onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => { window.removeEventListener('keydown', onKey); release('setup-draft-dock'); });
defineExpose({collapse, expand});
</script>

<template>
  <div v-if="visible" class="sdd" data-testid="setup-draft-dock">
    <div v-if="expanded" class="sdd-scrim" data-testid="setup-draft-dock-scrim" @click="collapse"/>
    <transition name="sdd-pop">
      <div v-if="expanded" class="sdd-panel" role="dialog" aria-label="Your draft">
        <div class="sdd-panel-body" data-testid="setup-draft-dock-body">
          <slot :collapse="collapse"/>
        </div>
        <div v-if="$slots.foot" class="sdd-panel-foot" data-testid="setup-draft-dock-foot">
          <slot name="foot" :collapse="collapse"/>
        </div>
      </div>
    </transition>
    <button
        v-if="!expanded"
        type="button"
        :key="`bar-${pulse}`"
        ref="bar"
        class="sdd-bar"
        :class="{'sdd-bar--fresh': fresh, 'sdd-bar--ready': pulsing, 'sdd-bar--ready-still': still}"
        :data-ready="ready ? 'true' : undefined"
        :aria-label="`${title}${fresh ? ', updated' : ''}${ready ? `, ${line}` : ''}. Open the draft`"
        @click="expand"
    >
      <v-icon size="18" :icon="tile?.icon ?? 'mdi-clipboard-text-outline'" class="sdd-icon" :style="tile ? {color: tile.ink} : undefined" :data-format="tile?.format ?? undefined"/>
      <span class="sdd-words">
        <span class="sdd-title">{{ title }}</span>
        <span v-if="line" class="sdd-line">{{ line }}</span>
      </span>
      <span v-if="fresh" class="sdd-dot" aria-hidden="true"/>
      <v-icon size="20" icon="mdi-chevron-up" class="sdd-chev"/>
    </button>
  </div>
</template>

<style scoped lang="scss">
/* The bar floats at the bottom-right of the page's transcript stage (position: relative), just above the composer.
   X181: the stage is the full-width conversation between the fixed top and the fixed composer (and clips), so the bar
   keeps 16px off its right edge (10px each side on a phone, the transcript's own gutters) and its shadow shows whole. */
.sdd {
  position: absolute;
  right: 16px;
  bottom: 8px;
  z-index: 5;
  width: min(340px, calc(100% - 32px));

  /* phones: a slim bar the width of the conversation */
  @media (max-width: 599px) {
    left: 10px;
    right: 10px;
    width: auto;
  }
}

.sdd-bar {
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 14px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.14);
  font-family: inherit;
  color: rgb(var(--v-theme-text));
  text-align: left;
  cursor: pointer;

  @media (max-width: 599px) {
    height: 48px;
    border-radius: 12px;
  }

  &--fresh {
    border-color: rgba(var(--v-theme-primary), 0.5);
    animation: sdd-pulse 1.2s ease-out 2;
  }

  /* Ready for review: a brand outline, and a ring that grows out from the edge and fades, 1.8s a cycle, on a loop.
     The ring is its own layer behind the bar so the bar keeps its white fill and its shadow untouched. */
  &--ready,
  &--ready-still {
    position: relative;
    border-color: rgb(var(--v-theme-primary));
  }

  &--ready::after {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4);
    animation: sdd-ring 1.8s ease-out infinite;
  }
}

@keyframes sdd-ring {
  0% { box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4); }
  70% { box-shadow: 0 0 0 9px rgba(var(--v-theme-primary), 0); }
  100% { box-shadow: 0 0 0 9px rgba(var(--v-theme-primary), 0); }
}

@keyframes sdd-pulse {
  0% { box-shadow: 0 8px 28px rgba(15, 23, 42, 0.14), 0 0 0 0 rgba(var(--v-theme-primary), 0.35); }
  100% { box-shadow: 0 8px 28px rgba(15, 23, 42, 0.14), 0 0 0 10px rgba(var(--v-theme-primary), 0); }
}

@media (prefers-reduced-motion: reduce) {
  .sdd-bar--fresh { animation: none; }
  .sdd-bar--ready::after { animation: none; box-shadow: none; }
}

.sdd-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-primary));
}

.sdd-words {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sdd-title {
  font-size: 13.5px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sdd-line {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 599px) {
    display: none;
  }
}

.sdd-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
}

.sdd-chev {
  flex-shrink: 0;
  color: rgb(var(--v-theme-textSubtle));
}

/* A click outside collapses it; on a phone the page dims lightly behind the sheet */
.sdd-scrim {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: transparent;

  @media (max-width: 599px) {
    background: rgba(15, 23, 42, 0.18);
  }
}

/* Desktop and tablet: a tall panel anchored bottom-right; the body scrolls inside it, the foot stays put (TlDialog) */
.sdd-panel {
  position: fixed;
  right: 24px;
  bottom: calc(24px + env(safe-area-inset-bottom));
  z-index: 1001;
  width: min(420px, calc(100vw - 32px));
  max-height: calc(100vh - 112px - env(safe-area-inset-bottom));
  max-height: calc(100dvh - 112px - env(safe-area-inset-bottom));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18);

  /* phones: a bottom sheet */
  @media (max-width: 599px) {
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    max-height: 85vh;
    max-height: 85dvh;
    border-radius: 16px 16px 0 0;
    border-bottom: 0;
  }
}

/* the draft and the club notes: the only part that scrolls */
.sdd-panel-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px;

  /* a phone sheet with no foot (nothing drafted yet) keeps its last card above the home indicator */
  @media (max-width: 599px) {
    padding-bottom: calc(4px + env(safe-area-inset-bottom));
  }

  /* the panel's own cards sit flat inside it */
  :deep(.tl-card) {
    border: none;
    box-shadow: none;
    background: transparent;
    overflow: visible;
  }

  /* a light hairline between the draft and the club notes */
  :deep(.tl-card + .tl-card) {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
    border-radius: 0;
  }
}

/* the review action, pinned under the body at every height (its own hairline and padding are SetupDraftFoot's) */
.sdd-panel-foot {
  flex-shrink: 0;
}

.sdd-pop-enter-active,
.sdd-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.sdd-pop-enter-from,
.sdd-pop-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
